import React, { useState } from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { createUser } from '../../services/userApi';
import { getAllRoles } from '../../services/roleApi';
import { mapApiErrorToFieldErrors, getFieldError, getFormErrorMessage } from '../../utils/formErrors';
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#2a2a2a',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.4)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#ff6b00',
    },
    '& input': {
      color: '#ffffff',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
});

const initialFormState = {
  name: '',
  username: '',
  email: '',
  password: '',
  password_confirmation: '',
  roleId: '',
};

const UserCreateDrawer = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { roles } = useSelector(state => state.auth);
  const isAdmin = roles?.includes('admin');

  // Load available roles from the API so newly created roles appear in the dropdown
  React.useEffect(() => {
    let mounted = true;
    const loadRoles = async () => {
      try {
        const resp = await getAllRoles();
        const list = Array.isArray(resp) ? resp : resp?.data || [];
        if (mounted) setAvailableRoles(list);
      } catch (err) {
        // ignore: dropdown will fall back to hardcoded values
      }
    };
    loadRoles();
    return () => { mounted = false; };
  }, []);

  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/hookah-flavors" replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset the form state
  const resetForm = () => {
    setFormData(initialFormState);
    setError(null);
    setFieldErrors({});
    setSuccess(false);
    setLoading(false);
  };

  // Handle drawer close
  const handleClose = () => {
    resetForm();
    if (onClose) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.password_confirmation) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setFieldErrors({});

      const newUser = await createUser({
        ...formData,
        roles: [formData.roleId]
      });

      setSuccess(true);

      // Show success message for 1.5 seconds before closing
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Reset and close
      resetForm();
      if (onSuccess) {
        onSuccess(newUser);
      }
      onClose();
      setSuccess(false);

    } catch (err) {
      setFieldErrors(mapApiErrorToFieldErrors(err));
      setError(getFormErrorMessage(err, 'Failed to create user'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(open)}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '400px',
          maxWidth: '100%',
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" component="h2">
            Create New User
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(211, 47, 47, 0.1)', color: '#ff1744' }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, backgroundColor: 'rgba(46, 125, 50, 0.1)', color: '#00c853' }}>
            User created successfully!
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <StyledTextField
              fullWidth
              label="Name"
              name="name"
              autoComplete="new-name"
              inputProps={{ autoComplete: 'new-name' }}
              value={formData.name}
              onChange={handleChange}
              required
              variant="outlined"
              error={Boolean(getFieldError(fieldErrors, 'name'))}
              helperText={getFieldError(fieldErrors, 'name')}
            />

            <StyledTextField
              fullWidth
              label="Username"
              name="username"
              autoComplete="new-username"
              inputProps={{ autoComplete: 'new-username' }}
              value={formData.username}
              onChange={handleChange}
              required
              variant="outlined"
              error={Boolean(getFieldError(fieldErrors, 'username'))}
              helperText={getFieldError(fieldErrors, 'username')}
            />

            <StyledTextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              autoComplete="new-email"
              inputProps={{ autoComplete: 'new-email' }}
              value={formData.email}
              onChange={handleChange}
              required
              variant="outlined"
              error={Boolean(getFieldError(fieldErrors, 'email'))}
              helperText={getFieldError(fieldErrors, 'email')}
            />

            <StyledTextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              inputProps={{ autoComplete: 'new-password' }}
              value={formData.password}
              onChange={handleChange}
              required
              variant="outlined"
              error={Boolean(getFieldError(fieldErrors, 'password'))}
              helperText={getFieldError(fieldErrors, 'password')}
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    sx={{ color: '#ff6b00' }}
                    tabIndex={-1}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )
              }}
            />

            <StyledTextField
              fullWidth
              label="Confirm Password"
              name="password_confirmation"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              inputProps={{ autoComplete: 'new-password' }}
              value={formData.password_confirmation}
              onChange={handleChange}
              required
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    edge="end"
                    sx={{ color: '#ff6b00' }}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )
              }}
            />

            <FormControl fullWidth variant="outlined">
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Role</InputLabel>
              <Select
                name="roleId"
                value={formData.roleId}
                onChange={(e) => setFormData(prev => ({ ...prev, roleId: e.target.value }))}
                required
                label="Role"
                sx={{
                  color: '#ffffff',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ff6b00' },
                  '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              >
                {availableRoles.length > 0 ? (
                  availableRoles.map(r => (
                    <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                  ))
                ) : (
                  <>
                    <MenuItem value={3}>User</MenuItem>
                    <MenuItem value={1}>Admin</MenuItem>
                  </>
                )}
              </Select>
            </FormControl>

            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                mt: 2,
                backgroundColor: '#ff6b00',
                '&:hover': {
                  backgroundColor: '#ff8533',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(255, 107, 0, 0.5)',
                },
              }}
            >
              {loading ? 'Creating...' : 'Create User'}
            </Button>
          </Stack>
        </form>
      </Box>
    </Drawer>
  );
};

export default UserCreateDrawer;